import { useGetAllInstrumentConfigurations } from '@hooks/queries/useGetAllInstrumentConfigurations';
import React from 'react';
import { Close } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  IconButton,
  LinearProgress,
  MenuItem,
  MenuList,
  Popover,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';

interface InstrumentSelectionsProps {
  instrumentId?: string;
  registrar?: string;
  onInstrumentChange?: (instrumentId: string, instrumentRegistrar: string) => void;
}
interface InstProps {
  instrumentId: string;
  registrar: string;
}
const InstrumentLayout: React.FC<InstProps> = ({ instrumentId, registrar }) => {
  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        sx={{
          width: '100%',
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        variant="body2"
        fontWeight="bold"
      >
        {instrumentId}
      </Typography>
      <Typography
        sx={{
          width: '100%',
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        variant="caption"
      >
        {registrar}
      </Typography>
    </Box>
  );
};

export const InstrumentSelections: React.FC<InstrumentSelectionsProps> = ({
  instrumentId,
  registrar,
  onInstrumentChange,
}) => {
  const instconfigs = useGetAllInstrumentConfigurations();
  const simpleInstConfigs =
    instconfigs.data?.instrumentConfigurations.map((i) => ({
      contractId: i.contract.contractId,
      registrar: i.payload.defaultIdentifier.source,
      instrumentId: i.payload.defaultIdentifier.id,
    })) || [];
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [selectedIndex, setSelectedIndex] = React.useState(1);
  const [internalInstrumentId, setInternalInstrumentId] = React.useState(instrumentId);
  const [internalRegistrar, setInternalRegistrar] = React.useState(registrar);
  const { isLoading } = instconfigs;
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const [input, setInput] = React.useState('');
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };
  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };
  const id = open ? 'instrument-select' : undefined;
  const handleMenuItemClick = (
    _: React.MouseEvent<HTMLElement>,
    index: number,
    selectedInstrumentId: string,
    selectedRegistrar: string,
  ) => {
    setSelectedIndex(index);
    setInternalInstrumentId(selectedInstrumentId);
    setInternalRegistrar(selectedRegistrar);
    onInstrumentChange?.(selectedInstrumentId, selectedRegistrar);
    handleClose();
  };
  const filteredConfigs = simpleInstConfigs.filter((i) =>
    i.instrumentId.toLowerCase().includes(input.toLowerCase()),
  );
  const hasSelectedInstrument = internalInstrumentId && internalRegistrar;
  return (
    <>
      <Box
        sx={(t) => ({
          cursor: onInstrumentChange ? 'pointer' : 'default',
          borderRadius: 1,
          border: `1px solid ${t.palette.divider}`,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          p: 1,
        })}
        onClick={onInstrumentChange ? handleClick : undefined}
      >
        <Box sx={{ width: '100%', overflow: 'hidden' }}>
          {hasSelectedInstrument ? (
            <InstrumentLayout instrumentId={internalInstrumentId} registrar={internalRegistrar} />
          ) : (
            <Typography sx={{ color: grey[500] }}>Select Instrument</Typography>
          )}
        </Box>

        {onInstrumentChange && (
          <IconButton sx={{ ml: 'auto' }} size="small">
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{ maxWidth: '600px' }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ display: 'relative', p: 1 }}>
          <Toolbar sx={{ alignItems: 'center' }} disableGutters variant="dense">
            <Typography fontWeight="bold">Select Instrument</Typography>
            <IconButton onClick={handleClose} sx={{ ml: 'auto' }}>
              <Close fontSize="small" color="action" />
            </IconButton>
          </Toolbar>
          <TextField
            onChange={onChange}
            value={input}
            sx={{ position: 'sticky', top: 0 }}
            size="small"
            autoComplete="off"
            placeholder="search by Instrument ID"
            fullWidth
          />
          {isLoading && <LinearProgress />}
          <Box sx={{ mt: 1, overflowY: 'auto', minWidth: 400 }}>
            <MenuList sx={{ mt: 0, maxHeight: 300 }}>
              {filteredConfigs?.map((instConfig, index) => (
                <MenuItem
                  onClick={(e) =>
                    handleMenuItemClick(e, index, instConfig.instrumentId, instConfig.registrar)
                  }
                  divider
                  disableGutters
                  selected={index === selectedIndex}
                  key={instConfig.contractId}
                >
                  <Box sx={{ p: 1, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    <InstrumentLayout
                      instrumentId={instConfig.instrumentId}
                      registrar={instConfig.registrar}
                    />
                  </Box>
                </MenuItem>
              ))}
            </MenuList>
            {filteredConfigs.length === 0 && (
              <Typography variant="body2" color="error">
                No instrument with ID {input} could be found
              </Typography>
            )}
          </Box>
        </Box>
      </Popover>
    </>
  );
};
